import React, { useState, useEffect } from 'react';
import styles from './EventTypeEditPopup.module.css';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import PropTypes from 'prop-types';
import CloseIcon from 'assets/images/icon_close.svg';
import BackIcon from 'assets/images/icon_arrow_right.svg';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';
import { getErrorMessage } from 'utils/api';
import { toast } from 'react-toastify';
import {
  createClubEventType,
  updateClubEventTypeByCode,
} from 'api/club/eventTypeApi';
import { BOOLEAN_OPTIONS } from 'constants/options';
import useChangeDetection from 'hooks/useChangeDetection';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';

export const categoryOptions = ['Track', 'Field', 'Training', 'Fun'].map(
  (item) => ({
    label: item,
    value: item,
  })
);
export const classificationOptions = [
  'Sprint',
  'Distance',
  'Hurdles',
  'Relay',
  'Walk',
  'Height',
  'Agility',
].map((item) => ({
  label: item,
  value: item,
}));

const EventTypeEditPopup = ({
  isOpen,
  onClose,
  clubID,
  eventType,
  mode = 'edit', // 'edit', 'add'
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    displayName: '',
    category: '',
    classification: '',
    distanceInMeters: undefined,
    isMultiLaneFinish: false,
    isVisible: false,
    sortOrder: '',
  });

  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState(null);

  // Change detection
  const currentData = {
    formData,
  };

  const { showPrompt, confirmLeave, cancelLeave, hasChanges, setShowPrompt } =
    useChangeDetection(originalData, currentData, isOpen);

  // Load data when editing
  useEffect(() => {
    if (mode === 'edit' && eventType) {
      const initialFormData = {
        displayName: eventType.displayName || '',
        category: eventType.category || '',
        classification: eventType.classification || '',
        distanceInMeters: eventType.distanceInMeters || '',
        isMultiLaneFinish: eventType.isMultiLaneFinish || false,
        isVisible: eventType.isActive || false,
        sortOrder: eventType.sortOrder || '',
      };
      setFormData(initialFormData);
      setOriginalData({
        formData: initialFormData,
      });
    } else {
      const initialFormData = {
        displayName: '',
        category: '',
        classification: '',
        distanceInMeters: '',
        isMultiLaneFinish: false,
        isVisible: false,
        sortOrder: '',
      };
      setFormData(initialFormData);
      setOriginalData({
        formData: initialFormData,
      });
    }
    setErrors({});
  }, [mode, eventType]);

  const handleInputChange = (name, value) => {
    if (name === 'sortOrder') {
      const numValue = parseInt(value);
      if (
        value &&
        value.toString().trim() &&
        !isNaN(numValue) &&
        numValue < 0
      ) {
        setErrors((prev) => ({
          ...prev,
          sortOrder: 'The sort order cannot be less than 0',
        }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    // Only validate the editable fields: sortOrder and isVisible
    if (!formData.sortOrder || formData.sortOrder.toString().trim() === '') {
      newErrors.sortOrder = 'The sort order is required';
    } else if (isNaN(parseInt(formData.sortOrder))) {
      newErrors.sortOrder = 'The sort order must be a valid number';
    } else if (parseInt(formData.sortOrder) < 0) {
      newErrors.sortOrder = 'The sort order cannot be less than 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        isActive: formData.isVisible, // Map isVisible to isActive for API
        distanceInMeters: +formData.distanceInMeters,
        sortOrder: parseInt(formData.sortOrder),
      };
      delete payload.isVisible;
      if (mode === 'edit') {
        await updateClubEventTypeByCode(clubID, eventType.eventCode, payload);
      } else {
        await createClubEventType(clubID, payload);
      }
      setIsLoading(false);
      onClose(true);
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (refresh) => {
    if (hasChanges()) {
      setShowPrompt(true);
      return;
    }
    onClose(refresh);
  };

  const handleConfirmLeave = () => {
    confirmLeave();
    onClose(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modalOverlay">
        <div className="modal bigModal">
          <div className="modalContent">
            <div className="modalTitle">
              {mode === 'add' ? 'Add Event Type' : 'Edit Event Type'}
            </div>
            <div className={styles.form}>
              {/* Read-only Event Type Information */}
              <div className={styles.readOnlySection}>
                <h3 className={styles.sectionTitle}>Event Type Information</h3>
                <div className={styles.infoGrid}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Event Name:</span>
                    <span className={styles.infoValue}>{formData.displayName}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Category:</span>
                    <span className={styles.infoValue}>{formData.category}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Classification:</span>
                    <span className={styles.infoValue}>{formData.classification}</span>
                  </div>
                  {formData.category !== 'Field' && (
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>Distance:</span>
                      <span className={styles.infoValue}>
                        {formData.distanceInMeters ? `${formData.distanceInMeters}m` : '-'}
                      </span>
                    </div>
                  )}
                  {formData.category !== 'Field' && (
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>Multi Lane Finish:</span>
                      <span className={styles.infoValue}>
                        {formData.isMultiLaneFinish ? 'Yes' : 'No'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Editable Fields Section */}
              <div className={styles.editableSection}>
                <div className={styles.twoColumnRow}>
                  <div className={styles.flex1}>
                    <div className={styles.inputGroup}>
                      <CustomDropdown
                        label="Visible"
                        name="isVisible"
                        value={formData.isVisible}
                        onChange={handleInputChange}
                        options={BOOLEAN_OPTIONS}
                        error={errors.isVisible}
                        disabled={isLoading}
                        placeholder=""
                      />
                      {errors.isVisible && (
                        <span className={styles.errorText}>{errors.isVisible}</span>
                      )}
                      <div className={styles.helpText}>
                        Select Yes to Show in event program group lists, select No
                        to hide.
                      </div>
                    </div>
                  </div>
                  <div className={styles.flex1}>
                    <div className={styles.inputGroup}>
                      <label className={styles.label}>Sort Order</label>
                      <input
                        type="number"
                        value={formData.sortOrder}
                        onChange={(e) =>
                          handleInputChange('sortOrder', e.target.value)
                        }
                        className={`${styles.input} ${errors.sortOrder ? styles.inputError : ''}`}
                        disabled={isLoading}
                        placeholder="Enter Sort Order"
                      />
                      {errors.sortOrder && (
                        <span className={styles.errorText}>
                          {errors.sortOrder}
                        </span>
                      )}
                      <div className={styles.helpText}>
                        Controls the display order of this event type in lists and menus. Lower numbers appear first.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.buttons}>
                <CustomNewButton
                  onClick={handleSave}
                  label="Save"
                  large={true}
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="goBack" onClick={() => handleClose(false)}>
              <div className="goBackImage">
                <img src={BackIcon} alt="back" />
              </div>
              <div className="goBackText">Back</div>
            </div>
            <div className="closeBtn" onClick={() => handleClose(false)}>
              <img src={CloseIcon} alt="close" />
            </div>
          </div>
        </div>
      </div>

      {/* Change Detection Confirmation Dialog */}
      <CustomNewConfirm
        isOpen={showPrompt}
        onClose={cancelLeave}
        title="Discard Changes?"
        description="You have unsaved changes. Are you sure you want to leave without saving?"
        onConfirm={handleConfirmLeave}
        confirmButtonText="Discard Changes"
        confirmButtonVariant="danger"
        onCancel={cancelLeave}
        cancelButtonText="Continue Editing"
      />
    </>
  );
};

EventTypeEditPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clubID: PropTypes.string.isRequired,
  mode: PropTypes.string,
  eventType: PropTypes.object,
};

export default EventTypeEditPopup;
